import React, { useState, useEffect } from 'react';
import { useToast } from '../../../context/ToastContext.jsx';
import server from '../../../api/server';

const MAX_FILE_SIZE_MB = 10;

const CreateKnowledgeBaseModal = ({clientID, emailID, onClose, onSave, isEdit, selectedKnowledgeBase}) => {
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [file_name, setFileName] = useState('');
  const [content_type, setContentType] = useState('');
  const [client_id, setClient_id] = useState(null);
  const [email, setEmail] = useState(null);
  const [knowledge_base_id, setKnowledgeBaseID] = useState(null);
  const [loading, setLoading] = useState(false);
  const [numberOfSearches, setNumberOfSearches] = useState(6);
  const [chunkSize, setChunkSize] = useState(350);
  const [chunkOverlap, setChunkOverlap] = useState(60);
  const [filePathChange, setFilePathChange] = useState(false);
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) {
      setPdfFile(null);
      setFileError('');
      return;
    }

    const isPdf = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));
    const isSizeOk = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

    if (!isPdf) {
      setPdfFile(null);
      setFileError('Only PDF files are allowed');
      return;
    }
    if (!isSizeOk) {
      setPdfFile(null);
      setFileError(`Max file size is ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    setFileError('');
    setPdfFile(file);
    setFileName(file.name);
    setContentType(file.type);
    setFilePathChange(true);
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim() || !description.trim() || !pdfFile) {
      showToast('Please fill all the fields and upload a PDF', 'error');
      return;
    }
    if(numberOfSearches < 3 || numberOfSearches > 12){
      showToast('Number of searches must be between 3 and 12', 'error');
      return;
    }
    try {
      setLoading(true);
      let formData = new FormData();
      formData.append('client_id', client_id);
      formData.append('email', email);
      formData.append('menu', 'voice-agents');
      formData.append('submenu', 'knowledge-base');
      formData.append('display_name', name);
      formData.append('description', description);
      formData.append('number_of_searches', numberOfSearches);
      formData.append('chunk_size', chunkSize);
      formData.append('chunk_overlap', chunkOverlap);
      formData.append('file', pdfFile);
      formData.append('file_name', file_name);
      formData.append('content_type', content_type);
      formData.append('knowledge_base_id', knowledge_base_id);
      formData.append('file_path_change', filePathChange);
      let res = await server.createKnowledgeBase(formData)
      let resp = await res.json()
      if(res.status === 200){
        showToast(resp.message, 'success');
      }else{
        showToast(resp.message || resp.error, 'error');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
    onSave();
  };

  useEffect(() => {
    if(isEdit && selectedKnowledgeBase){
      setName(selectedKnowledgeBase.display_name)
      setDescription(selectedKnowledgeBase.description)
      setKnowledgeBaseID(selectedKnowledgeBase.knowledge_base_id)
      setNumberOfSearches(selectedKnowledgeBase.number_of_searches)
      setPdfFile(selectedKnowledgeBase.file_path)
      setChunkSize(selectedKnowledgeBase.chunk_size)
      setChunkOverlap(selectedKnowledgeBase.chunk_overlap)
    }
    if(clientID && emailID){
      setClient_id(clientID);
      setEmail(emailID);
    }
  }, [isEdit, selectedKnowledgeBase, clientID, emailID])

  return (
    <div className="p-4">
      {!loading && <div style={{height: "75vh"}}>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Name</label>
        <input
          type="text"
          value={name} disabled={isEdit}
          onChange={(e) => setName(e.target.value)}
          className="global-input rounded-md p-1 w-full"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Description</label>
        <textarea rows="4"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="global-input rounded-md p-1 w-full"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Number of results to be retrieved</label>
        <input
          type="number" max={12} min={3}
          value={numberOfSearches}
          onChange={(e) => setNumberOfSearches(e.target.value)}
          className="global-input rounded-md p-1 w-full"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Chunk Size</label>
        <input
          type="number"
          value={chunkSize} disabled={isEdit}
          onChange={(e) => setChunkSize(e.target.value)}
          className="global-input rounded-md p-1 w-full"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Chunk Overlap</label>
        <input
          type="number"
          value={chunkOverlap} disabled={isEdit}
          onChange={(e) => setChunkOverlap(e.target.value)}
          className="global-input rounded-md p-1 w-full"
        />
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">Upload PDF</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className="global-input rounded-md p-1 w-full"
          />
        <div className="text-xs text-gray-500 mt-1">PDF only, max {MAX_FILE_SIZE_MB} MB</div>
        {fileError !== '' && (
          <div className="mt-1" style={{fontSize: '12px', color: 'red'}}>{fileError}</div>
        )}
      </div>
      </div>}
      {
        loading && (
          <div className="flex justify-center items-center" style={{ height: "70vh", flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>
        )
      }
      <div className="mb-2">
      <p className='text-xs text-blue-600'> Note: Maximum 30 knowledge bases can be created per client.</p>
      </div>

      <hr/>
      <div className="flex justify-end gap-3 py-3">
        <button onClick={handleCancel} className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50" style={{height: '40px', borderRadius: '4px'}}>
          Cancel
        </button>
        <button onClick={handleSave} className="px-6 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700" style={{height: '40px', borderRadius: '4px'}}>
          Save
        </button>
      </div>
    </div>
  )
}

export default CreateKnowledgeBaseModal;